import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import GlobalButton from "../../../components/button";
import AppHeader from "../../../components/header";
import { getALlWebUsers } from "../../../services/WebUser";
import { handleOpenSnackBar } from "../../../utils/helper";
import AppLoader from "../../../components/loader";

// const socket = io.connect("https://sirldigital.com/trust_telecom");
const socket = io.connect(process.env.REACT_APP_SOCKET_URL);

const AdminChatPage = () => {
  const chatEndRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({});
  const fetchAllWebUsers = async () => {
    setIsLoading(true);
    try {
      const result = await getALlWebUsers();
      if (result.data.status === "success") {
        setUsers(result.data.data);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAllWebUsers();
    socket.emit("adminJoin");
    const storedUnreadMessages = localStorage.getItem("unreadMessages");
    if (storedUnreadMessages) {
      setUnreadMessages(JSON.parse(storedUnreadMessages));
    }
  }, []);
  useEffect(() => {
    socket.on("updateUserList", (userList) => {
      setUsers(userList);
    });
    socket.on("adminMessage", (msg) => {
      setUnreadMessages((prev) => {
        const updatedUnreadMessages = {
          ...prev,
          [msg.sender]: {
            count: (prev[msg.sender]?.count || 0) + 1,
            latestTime: new Date(), // Set the latest message time
          },
        };
        // Save to localStorage
        localStorage.setItem(
          "unreadMessages",
          JSON.stringify(updatedUnreadMessages)
        );
        return updatedUnreadMessages;
      });
      if (selectedUser && selectedUser?.userId === msg.sender) {
        setChat((prevChat) => [
          ...prevChat,
          { sender: msg.sender, message: msg.message, createdAt: new Date() },
        ]);
        setUnreadMessages((pre) => ({
          ...pre,
          [msg.sender]: { count: 0, latestTime: null }, // Reset unread count and time
        }));
        localStorage.setItem(
          "unreadMessages",
          JSON.stringify({
            ...unreadMessages,
            [msg.sender]: { count: 0, latestTime: null },
          })
        );
      }
    });
    socket.on("chatHistory", (messages) => {
      setChat(messages);
    });
    return () => {
      socket.off("updateUserList");
      socket.off("adminMessage");
      socket.off("chatHistory");
    };
  }, [selectedUser]);
  const sendResponse = () => {
    if (selectedUser && message) {
      socket.emit("adminResponse", { message, userId: selectedUser.userId });
      setChat((prevChat) => [
        ...prevChat,
        { sender: "admin", message, createdAt: new Date() },
      ]);
      setMessage("");
    }
  };
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setChat([]);
    socket.emit("requestChatHistory", user.userId);
    setUnreadMessages((prev) => {
      const updatedUnreadMessages = {
        ...prev,
        [user.userId]: { count: 0, latestTime: null },
      };
      localStorage.setItem(
        "unreadMessages",
        JSON.stringify(updatedUnreadMessages)
      );
      return updatedUnreadMessages;
    });
  };
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat]);
  // Sort users based on latest message timestamp
  // Sort users based on latest message timestamp
  const sortedUsers = users.sort((a, b) => {
    const aLatest = unreadMessages[a.userId]?.latestTime
      ? new Date(unreadMessages[a.userId].latestTime).getTime()
      : 0;
    const bLatest = unreadMessages[b.userId]?.latestTime
      ? new Date(unreadMessages[b.userId].latestTime).getTime()
      : 0;
    return bLatest - aLatest; // Sort descending by latest message timestamp
  });
  return (
    <Stack height={"100vh"}>
      <AppLoader isVisible={isLoading} />
      <AppHeader />
      <Box bgcolor={"#f3f3f3"} flexGrow={1} padding={3}>
        <Stack height={"100%"} direction={"row"} border={"3px solid white"}>
          <Stack flex={0.6} bgcolor={"white"}>
            <Stack
              p={1.5}
              bgcolor={"#ffd16e"}
              direction={"row"}
              alignItems={"center"}
              gap={1}
            >
              <Avatar>
                <PeopleAltIcon />
              </Avatar>
              <Typography
                typography={"body1"}
                color={"secondary.light"}
                fontWeight={500}
              >
                All Users
              </Typography>
            </Stack>
            <List>
              {sortedUsers.map((user) => {
                const isActive = selectedUser?.userId === user.userId;
                const unreadCount = unreadMessages[user.userId]?.count || 0;
                return (
                  <ListItemButton
                    sx={{
                      backgroundColor: isActive ? "#ffd16e" : "primary.light",
                      borderBottom: "1px solid lightgrey",
                    }}
                    key={user.socketId}
                    onClick={() => handleUserSelect(user)}
                  >
                    <ListItemText
                      primary={user.userId}
                      secondary={
                        unreadCount > 0 ? `${unreadCount} new messages` : ""
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Stack>
          <Stack
            flex={2}
            justifyContent={"space-between"}
            borderLeft={"3px solid white"}
          >
            {selectedUser && (
              <>
                <Stack
                  p={1.5}
                  bgcolor={"primary.main"}
                  direction={"row"}
                  alignItems={"center"}
                  gap={1}
                >
                  <Avatar>{selectedUser?.userId.charAt(0)}</Avatar>
                  <Typography
                    typography={"body1"}
                    color={"secondary.light"}
                    fontWeight={500}
                  >
                    {`Chat With ${selectedUser?.userId}`}
                  </Typography>
                </Stack>
                <Stack
                  flexGrow={1}
                  spacing={1.5}
                  px={2}
                  pt={1}
                  sx={{
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 250px)",
                  }}
                >
                  {chat.map((msg, index) => {
                    const isSender = msg.sender === "admin";
                    const createdTime = msg?.createdAt;
                    return (
                      <Stack
                        p={1}
                        key={index}
                        borderRadius={"6px"}
                        maxWidth={"320px"}
                        bgcolor={isSender ? "#e1faff" : "#fffc8f"}
                        alignSelf={isSender ? "flex-end" : "flex-start"}
                      >
                        <Typography variant="body1" fontWeight={500}>
                          {isSender ? "Admin" : "User"}
                        </Typography>
                        <Typography variant="body1">{msg.message}</Typography>
                        <Typography mt={0.3} fontSize={"0.7rem"}>
                          {moment(createdTime).format("DD.MM.YY [at] HH:MM")}
                        </Typography>
                      </Stack>
                    );
                  })}
                  <div ref={chatEndRef} />
                </Stack>
                <Stack
                  direction={"row"}
                  borderTop={"1px solid grey"}
                  bgcolor={"secondary.light"}
                >
                  <TextField
                    fullWidth
                    placeholder={"Enter your message or reply"}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none !important",
                        outline: "none !important",
                      },
                    }}
                  />
                  <GlobalButton
                    handleClickBtn={sendResponse}
                    startIcon={<SendIcon />}
                    style={{
                      width: "auto",
                      borderRadius: 0,
                    }}
                  />
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default AdminChatPage;
